/**
 * The /recordings surface: a two-pane archive - LEFT the recordings list (room, date,
 * duration, status; the server's newest-first order), RIGHT the watch window with the big
 * player. Selecting a list entry opens it; the overview is the only playback tab (the raw
 * merged/video/audio/mp3 tabs are gone - the retention prune deletes them anyway), the
 * pending overview shows «Общий план готовится» until the worker publishes it, and a
 * recording with no overview row shows the watch_no_media empty state. Media streams through
 * the signed streamUrl into the video.js player (lazy chunk: seek/volume/speed/PiP/
 * fullscreen controls); the bearer-blob fallback and the one-shot 401 URL regeneration stay.
 * The Диалоги tab renders the first channel transcript; «Полная транскрибация» merges every
 * channel's transcript into one chronological feed with resolved speaker names (CR-35);
 * Саммари shows the DeepSeek summary. The list polls every 15 s while any recording is still
 * moving through the worker pipeline. The ?room=<slug> query parameter preseeds the room
 * search - the webinar catalog's «Прошедшие» rows deep-link with it.
 */

import {
  AlertCircle,
  ArrowLeft,
  Download,
  FileVideo2,
  LoaderCircle,
  RefreshCw,
  Search,
  SearchX,
  X,
} from 'lucide-react';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../components/ui/button';

import {
  fetchRecordingFile,
  fetchRecordingFileProgress,
  recordingFileNameByPurpose,
  RecordingApiError,
  retrySummary,
} from '../api/recording';
import type { RecordingFile, RecordingFilePurpose } from '../api/recording';
import {
  browserSsoStorage,
  callWithSsoRecovery,
  loadSsoSession,
  startSsoAutoRefresh,
} from '../auth/sso';
import { showToast, ToastStack } from '../calendar/Toast';
import {
  checkSession,
  forgetSession,
  startCalendarSignIn,
  calendarSsoConfig,
} from '../calendar/signIn';
import { navigateTo } from '../lib/spa';
import { formatFileSize, recordingDuration } from '../room/RecordingsPanel';
import { formatMeetingTimer } from '../room/roomState';
import type { KeyValueStorage } from '../room/devicePrefs';
import { cn } from '../lib/utils';
import { isActiveRoomRecording, listAllRecordings } from './api';
import type { RoomRecording } from './api';
import {
  FullTranscriptView,
  parseTranscript,
  transcriptTime,
  useFullTranscript,
} from './FullTranscript';
import type { TranscriptDoc } from './FullTranscript';

// The video.js player is a split chunk: player code + skin CSS only load once an
// artifact actually plays (same pattern as the lazy calendar page in App.tsx).
const RecordingPlayer = lazy(async () => {
  const module = await import('./RecordingPlayer');
  return { default: module.RecordingPlayer };
});

/** Poll interval while any recording on the archive is still not in a terminal state. */
const RECORDINGS_POLL_MS = 15_000;

type StatusFilter = 'all' | 'active' | 'completed' | 'failed';

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly recordings: readonly RoomRecording[] }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error' };

/** The watch window's playback tabs: only the composite overview remains playable (the raw
 * merged/video/audio/mp3 artifacts are pruned by retention once overview + transcript land).
 * Диалоги, «Полная транскрибация» and Саммари are static (below) - they exist for every
 * recording so their pipeline states (transcribing / preparing / no key) are visible before
 * the artifacts land. */
const TAB_ORDER: readonly RecordingFilePurpose[] = ['overview'];

export interface RecordingsPageProps {
  /** Injected for tests; defaults to the browser's shared SSO store (localStorage-backed). */
  readonly storage?: KeyValueStorage | null;
  /** Injected for tests; defaults to the wall clock. */
  readonly now?: Date;
  /** Fetch override for tests; defaults to the global fetch. */
  readonly fetchImpl?: typeof fetch;
  /** Poll interval override for tests; production keeps 15 s. */
  readonly pollMs?: number | undefined;
}

export function RecordingsPage({
  storage,
  now,
  fetchImpl,
  pollMs,
}: RecordingsPageProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const store = useMemo(() => storage ?? browserSsoStorage(), [storage]);
  const at = useMemo(() => now ?? new Date(), [now]);
  const nowMs = useCallback((): number => (now !== undefined ? now.getTime() : Date.now()), [now]);
  const doFetch = fetchImpl ?? globalThis.fetch;

  const [session, setSession] = useState<'signed_in' | 'guest'>(() =>
    store !== null && checkSession(store, at) === 'signed_in' ? 'signed_in' : 'guest',
  );
  const [token, setToken] = useState<string | null>(() =>
    store !== null && checkSession(store, at) === 'signed_in'
      ? (loadSsoSession(store)?.accessToken ?? null)
      : null,
  );

  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  // The room filter preseeds from ?room=<slug> - the webinar catalog's «Прошедшие» rows
  // deep-link here with the room's slug (CR-24). Kept in the URL: a reload keeps the filter.
  const [query, setQuery] = useState(() => {
    const room = new URLSearchParams(window.location.search).get('room');
    return room === null ? '' : room;
  });
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(null);
  /** The recording opened in the watch window (null until a list entry is selected). */
  const [selectedId, setSelectedId] = useState<number | null>(null);
  /** The recording whose summary retry is in flight (the Саммари tab's disabled Повторить). */
  const [summaryRetryingId, setSummaryRetryingId] = useState<number | null>(null);
  const refreshSeqRef = useRef(0);
  const loadStartedRef = useRef(false);

  // A bearer the service rejected after the recovery wrapper: the rotation chain is dead.
  const handleUnauthorized = useCallback((): void => {
    if (store !== null) {
      forgetSession(store);
    }
    setToken(null);
    setSession('guest');
  }, [store]);

  // Auto-refresh keeps the bearer alive while the tab is open (same contract as the dashboard).
  useEffect(() => {
    if (store === null || session !== 'signed_in') {
      return undefined;
    }
    return startSsoAutoRefresh({
      config: calendarSsoConfig(),
      storage: store,
      fetchImpl: doFetch,
      nowMs,
      onRefreshed: (freshToken) => setToken(freshToken),
      onInvalidGrant: handleUnauthorized,
    });
  }, [store, doFetch, handleUnauthorized, nowMs, session]);

  const refresh = useCallback((): void => {
    const currentToken = token;
    if (currentToken === null) {
      return;
    }
    const seq = refreshSeqRef.current + 1;
    refreshSeqRef.current = seq;
    callWithSsoRecovery(currentToken, (fresh) => listAllRecordings(fresh, doFetch), {
      config: calendarSsoConfig(),
      storage: store,
      fetchImpl: doFetch,
      rejected: (err: unknown): boolean => err instanceof RecordingApiError && err.status === 401,
      onRefreshed: (freshToken) => setToken(freshToken),
      nowMs,
    })
      .then((recordings) => {
        if (refreshSeqRef.current === seq) {
          setLoad({ kind: 'ready', recordings });
        }
      })
      .catch((err: unknown) => {
        if (refreshSeqRef.current !== seq) {
          return;
        }
        if (err instanceof RecordingApiError && err.status === 401) {
          handleUnauthorized();
          return;
        }
        if (err instanceof RecordingApiError && err.status === 403) {
          setLoad({ kind: 'forbidden' });
          return;
        }
        setLoad({ kind: 'error' });
      });
  }, [token, doFetch, store, nowMs, handleUnauthorized]);

  // One load per signed-in mount (StrictMode double-mounts guarded by the ref).
  useEffect(() => {
    if (session !== 'signed_in' || loadStartedRef.current) {
      return;
    }
    loadStartedRef.current = true;
    refresh();
  }, [session, refresh]);

  const recordings = load.kind === 'ready' ? load.recordings : [];
  const hasActive = recordings.some((recording) => isActiveStatus(recording.status));
  // The worker pipeline keeps moving after a recording goes terminal (overview pending,
  // transcript processing, the summary job) - the archive polls while ANY artifact row is
  // still in flight, so Диалоги/Саммари fill in without a manual refresh (CR-29/CR-30).
  const hasPendingWork = recordings.some((recording) =>
    recording.files.some((file) => file.status === 'pending' || file.status === 'processing'),
  );
  useEffect(() => {
    if ((!hasActive && !hasPendingWork) || load.kind !== 'ready') {
      return;
    }
    const timer = window.setInterval(() => {
      refresh();
    }, pollMs ?? RECORDINGS_POLL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [hasActive, hasPendingWork, load.kind, pollMs, refresh]);

  const handleSignIn = useCallback(async (): Promise<void> => {
    if (store === null) {
      showToast('error', t('sso.notice_storage_blocked'));
      return;
    }
    try {
      await startCalendarSignIn(
        calendarSsoConfig(),
        window.location.origin,
        store,
        window.location,
        '/',
      );
    } catch {
      // staying on the page; the button retries
    }
  }, [store, t]);

  // The Саммари tab's «Повторить» (CR-30): re-enqueues the summary job through the retry
  // route, then refreshes the list so the fresh state (or a prepared artifact) shows.
  const retrySelectedSummary = useCallback(
    (recording: RoomRecording): void => {
      const currentToken = token;
      if (currentToken === null || summaryRetryingId !== null) {
        return;
      }
      setSummaryRetryingId(recording.id);
      callWithSsoRecovery(
        currentToken,
        (fresh) => retrySummary(recording.roomSlug, recording.id, fresh, doFetch),
        {
          config: calendarSsoConfig(),
          storage: store,
          fetchImpl: doFetch,
          rejected: (err: unknown): boolean => err instanceof RecordingApiError && err.status === 401,
          onRefreshed: (freshToken) => setToken(freshToken),
          nowMs,
        },
      )
        .then(() => {
          setSummaryRetryingId(null);
          refresh();
        })
        .catch(() => {
          setSummaryRetryingId(null);
          showToast('error', t('recordings.summary_retry_failed'));
        });
    },
    [token, summaryRetryingId, doFetch, store, nowMs, refresh, t],
  );

  async function download(recording: RoomRecording, file: RecordingFile): Promise<void> {
    const currentToken = token;
    if (currentToken === null || downloadingFileId !== null) {
      return;
    }
    setDownloadingFileId(file.id);
    try {
      const blob = await fetchRecordingFile(
        recording.roomSlug,
        recording.id,
        file.id,
        currentToken,
        doFetch,
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = recordingFileNameByPurpose(recording.roomSlug, file.purpose);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    } catch (err: unknown) {
      showToast(
        'error',
        err instanceof RecordingApiError && err.code === 'not_found'
          ? t('recording.download_not_ready')
          : t('recording.download_failed'),
      );
    } finally {
      setDownloadingFileId(null);
    }
  }

  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const visible = useMemo(
    () => filterRecordings(recordings, query, filter),
    [recordings, query, filter],
  );
  const selected = useMemo(
    () => recordings.find((recording) => recording.id === selectedId) ?? null,
    [recordings, selectedId],
  );

  return (
    <div
      data-testid="recordings-page"
      className="flex h-screen flex-col overflow-hidden bg-background text-foreground"
    >
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-card px-4 sm:px-6">
        <Button
          variant="ghost"
          size="icon"
          className="size-9 text-muted-foreground hover:text-foreground"
          aria-label={t('recordings.back_aria')}
          data-testid="recordings-back"
          onClick={() => navigateTo('/')}
        >
          <ArrowLeft aria-hidden className="size-4" />
        </Button>
        <h1 className="truncate text-[15px] font-semibold tracking-tight text-foreground">
          {t('recordings.page_title')}
        </h1>
        {load.kind === 'ready' ? (
          <span
            data-testid="recordings-count"
            className="ml-1 rounded-full border border-border px-2 py-0.5 text-[10.5px] font-semibold tabular-nums text-muted-foreground"
          >
            {String(recordings.length)}
          </span>
        ) : null}
        <button
          type="button"
          data-testid="recordings-refresh"
          aria-label={t('recording.refresh_aria')}
          className="ml-auto grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
          disabled={session !== 'signed_in'}
          onClick={() => {
            refresh();
          }}
        >
          <RefreshCw aria-hidden className="size-4" />
        </button>
      </header>

      {session !== 'signed_in' || load.kind !== 'ready' ? (
        // Gate / loading / access / failure states keep the old single-column shape.
        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[920px] px-4 py-6 sm:px-6 lg:px-8">
            {session === 'guest' ? (
              <div
                className="flex flex-col items-start gap-3 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between"
                data-testid="recordings-gate"
              >
                <div className="min-w-0">
                  <p className="text-[14px] font-semibold text-foreground">
                    {t('recordings.gate.title')}
                  </p>
                  <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
                    {t('recordings.gate.description')}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="recordings-gate-signin"
                  className="shrink-0 rounded-lg bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                  onClick={() => {
                    void handleSignIn();
                  }}
                >
                  {t('calendar.gate.sign_in')}
                </button>
              </div>
            ) : load.kind === 'loading' ? (
              <div
                className="flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-[13px] text-muted-foreground"
                data-testid="recordings-loading"
              >
                {t('common.loading')}
              </div>
            ) : load.kind === 'forbidden' ? (
              <div
                role="alert"
                className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-[13px] text-amber-200"
                data-testid="recordings-forbidden"
              >
                {t('recordings.forbidden')}
              </div>
            ) : (
              <div
                role="alert"
                className="rounded-xl border border-border bg-card p-4 text-[13px] text-muted-foreground"
                data-testid="recordings-error"
              >
                {t('recording.load_failed')}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          {/* Sidebar: search, status chips, the archive list (server's newest-first order). */}
          <aside
            data-testid="recordings-sidebar"
            className="flex max-h-[45vh] w-full shrink-0 flex-col border-b border-border bg-card/40 lg:max-h-none lg:w-[350px] lg:border-b-0 lg:border-r"
          >
            <div className="shrink-0 space-y-2.5 border-b border-border px-3 py-3">
              <div className="relative">
                <Search
                  aria-hidden
                  className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t('recordings.search_placeholder')}
                  aria-label={t('recordings.search_aria')}
                  data-testid="recordings-search"
                  className="h-9 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-[13px] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                />
              </div>
              <div className="flex gap-1.5" role="group" aria-label={t('recordings.filter_aria')}>
                {FILTER_CHIPS.map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    data-testid={`recordings-filter-${chip.id}`}
                    aria-pressed={filter === chip.id}
                    onClick={() => setFilter(chip.id)}
                    className={cn(
                      'rounded-full px-2.5 py-1 text-[11.5px] font-medium transition-colors',
                      filter === chip.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-accent',
                    )}
                  >
                    {t(chip.labelKey)}
                  </button>
                ))}
              </div>
            </div>
            <nav
              data-testid="recordings-list"
              aria-label={t('recordings.list_aria')}
              className="scroll-thin min-h-0 flex-1 overflow-y-auto px-3 py-3"
            >
              {recordings.length === 0 ? (
                <div
                  className="flex flex-col items-center rounded-xl border border-border bg-card px-4 py-10 text-center"
                  data-testid="recordings-empty"
                >
                  <span className="flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <FileVideo2 aria-hidden className="size-4" />
                  </span>
                  <p className="mt-3 text-[13px] font-medium text-muted-foreground">
                    {t('recordings.empty')}
                  </p>
                  <p className="mt-1 text-[11.5px] text-muted-foreground/70">
                    {t('recordings.empty_hint')}
                  </p>
                </div>
              ) : visible.length === 0 ? (
                <div
                  className="flex flex-col items-center rounded-xl border border-border bg-card px-4 py-10 text-center"
                  data-testid="recordings-no-match"
                >
                  <span className="flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <SearchX aria-hidden className="size-4" />
                  </span>
                  <p className="mt-3 text-[13px] font-medium text-muted-foreground">
                    {t('recordings.no_match')}
                  </p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {visible.map((recording) => (
                    <li key={`${recording.roomSlug}:${String(recording.id)}`}>
                      <RecordingListItem
                        recording={recording}
                        locale={locale}
                        selected={recording.id === selectedId}
                        onSelect={() => {
                          setSelectedId(recording.id);
                        }}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </nav>
          </aside>

          {/* Watch window: the selected recording's player and artifact tabs. */}
          <main
            data-testid="recordings-watch"
            aria-label={t('recordings.watch_aria')}
            className="min-h-0 flex-1 overflow-y-auto"
          >
            {selected === null ? (
              <div
                data-testid="recordings-watch-empty"
                className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center"
              >
                <span className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <FileVideo2 aria-hidden className="size-5" />
                </span>
                <p className="text-[13.5px] font-medium text-muted-foreground">
                  {t('recordings.select_hint')}
                </p>
              </div>
            ) : (
              <WatchWindow
                key={`${selected.roomSlug}:${String(selected.id)}`}
                recording={selected}
                locale={locale}
                token={token ?? ''}
                fetchImpl={doFetch}
                downloadingFileId={downloadingFileId}
                onDownload={(file) => {
                  void download(selected, file);
                }}
                onClose={() => {
                  setSelectedId(null);
                }}
                onRefresh={() => {
                  refresh();
                }}
                onRetrySummary={() => {
                  void retrySelectedSummary(selected);
                }}
                summaryRetryInFlight={summaryRetryingId === selected.id}
              />
            )}
          </main>
        </div>
      )}

      <ToastStack />
    </div>
  );
}

const FILTER_CHIPS: readonly {
  readonly id: StatusFilter;
  readonly labelKey: string;
}[] = [
  { id: 'all', labelKey: 'recordings.filter_all' },
  { id: 'active', labelKey: 'recordings.filter_active' },
  { id: 'completed', labelKey: 'recordings.filter_completed' },
  { id: 'failed', labelKey: 'recordings.filter_failed' },
];

function isActiveStatus(status: RoomRecording['status']): boolean {
  return isActiveRoomRecording(status);
}

function matchesFilter(status: RoomRecording['status'], filter: StatusFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'active':
      return isActiveStatus(status);
    case 'completed':
      return status === 'completed';
    case 'failed':
      return status === 'failed';
  }
}

/** Search (by room slug) and status filtering over the archive. */
function filterRecordings(
  recordings: readonly RoomRecording[],
  query: string,
  filter: StatusFilter,
): readonly RoomRecording[] {
  const needle = query.trim().toLowerCase();
  return recordings.filter(
    (recording) =>
      recording.roomSlug.toLowerCase().includes(needle) && matchesFilter(recording.status, filter),
  );
}

const STATUS_TONE: Readonly<Record<RoomRecording['status'], string>> = {
  starting: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  recording: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  stopping: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  completed: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  failed: 'border-red-500/40 bg-red-500/10 text-red-300',
};

/** One sidebar entry: room, id, start time, duration and the status badge. */
function RecordingListItem(props: {
  readonly recording: RoomRecording;
  readonly locale: string;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const durationMs = recordingDuration(props.recording);
  return (
    <button
      type="button"
      data-testid={`recording-row-${String(props.recording.id)}`}
      aria-pressed={props.selected}
      onClick={props.onSelect}
      className={cn(
        'w-full rounded-xl border px-3 py-2.5 text-left transition-colors',
        props.selected
          ? 'border-primary/60 bg-accent'
          : 'border-transparent hover:border-border hover:bg-accent/50',
      )}
    >
      <span className="flex items-center gap-2">
        <span className="min-w-0 truncate font-mono text-[12.5px] font-semibold text-foreground">
          /r/{props.recording.roomSlug}
        </span>
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
          #{String(props.recording.id)}
        </span>
        <span
          data-testid={`rec-status-${String(props.recording.id)}`}
          className={cn(
            'ml-auto shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium',
            STATUS_TONE[props.recording.status],
          )}
        >
          {t(`recording.state_${props.recording.status}`)}
        </span>
      </span>
      <span className="mt-1 flex items-center gap-2 text-[11.5px] tabular-nums text-muted-foreground">
        <span>
          {new Intl.DateTimeFormat(props.locale, {
            dateStyle: 'medium',
            timeStyle: 'short',
          }).format(new Date(props.recording.startedAt))}
        </span>
        {durationMs !== null ? <span>{formatMeetingTimer(Math.round(durationMs / 1000))}</span> : null}
      </span>
      {props.recording.error !== null ? (
        <span role="alert" className="mt-1 block truncate text-[11px] text-red-300">
          {props.recording.error}
        </span>
      ) : null}
    </button>
  );
}

interface WatchWindowProps {
  readonly recording: RoomRecording;
  readonly locale: string;
  readonly token: string;
  readonly fetchImpl: typeof fetch;
  readonly downloadingFileId: number | null;
  readonly onDownload: (file: RecordingFile) => void;
  readonly onClose: () => void;
  /** Refreshes the archive list - the signed stream URL regeneration on expiry (CR-29). */
  readonly onRefresh: () => void;
  /** Re-enqueues the summary job - the Саммари tab's «Повторить» (CR-30). */
  readonly onRetrySummary: () => void;
  readonly summaryRetryInFlight: boolean;
}

/** Active watch-window tab: the overview artifact row, one of the static pipeline tabs,
 * or the empty state (the recording has no overview row to play yet). */
type ActiveTab =
  | { readonly kind: 'file'; readonly purpose: RecordingFilePurpose; readonly file: RecordingFile }
  | { readonly kind: 'transcript' }
  | { readonly kind: 'full' }
  | { readonly kind: 'summary' }
  | { readonly kind: 'empty' };

function sameTab(a: ActiveTab, b: ActiveTab): boolean {
  if (a.kind !== b.kind) {
    return false;
  }
  if (a.kind === 'file' && b.kind === 'file') {
    return a.file.id === b.file.id;
  }
  return true;
}

/**
 * The watch window: title bar, the artifact tabs (the overview row the recording actually
 * has, plus the always-present Диалоги / «Полная транскрибация» / Саммари pipeline tabs),
 * the big video.js player - the overview streams through the signed streamUrl (CR-29; the
 * bearer blob path stays the fallback) - and the download button of the active artifact.
 */
function WatchWindow(props: WatchWindowProps): JSX.Element {
  const { t } = useTranslation();
  const { recording } = props;
  const durationMs = recordingDuration(recording);

  // The overview row (the only playable artifact); Диалоги/Полная/Саммари are static and
  // exist for every recording.
  const fileTabs = useMemo(() => {
    return TAB_ORDER.flatMap((purpose) =>
      recording.files
        .filter((file) => file.purpose === purpose)
        .sort((a, b) => a.id - b.id)
        .map((file) => ({ purpose, file })),
    );
  }, [recording.files]);
  const tabs: readonly ActiveTab[] = useMemo(
    () => [
      ...fileTabs.map((tab): ActiveTab => ({ kind: 'file', purpose: tab.purpose, file: tab.file })),
      { kind: 'transcript' },
      { kind: 'full' },
      { kind: 'summary' },
    ],
    [fileTabs],
  );

  const transcriptFile = useMemo(
    () => recording.files.find((file) => file.purpose === 'transcript') ?? null,
    [recording.files],
  );
  const summaryFile = useMemo(
    () => recording.files.find((file) => file.purpose === 'summary') ?? null,
    [recording.files],
  );
  const transcriptReady = transcriptFile !== null && transcriptFile.status === 'ready';

  // The overview row (ready or still preparing) is the default tab; a recording with no
  // overview row opens on the empty state.
  const defaultTab = useMemo<ActiveTab>(() => {
    const overview = fileTabs[0];
    return overview !== undefined
      ? { kind: 'file', purpose: overview.purpose, file: overview.file }
      : { kind: 'empty' };
  }, [fileTabs]);

  const [activeTab, setActiveTab] = useState<ActiveTab>(defaultTab);
  const current: ActiveTab =
    tabs.find((tab) => sameTab(tab, activeTab)) ?? defaultTab;

  const activeFile = current.kind === 'file' ? current.file : null;
  const activePurpose = current.kind === 'file' ? current.purpose : null;

  const [mediaLoad, setMediaLoad] = useState<MediaLoad>({ kind: 'idle' });
  const [transcript, setTranscript] = useState<TranscriptLoad>({ kind: 'idle' });
  const [summary, setSummary] = useState<SummaryLoad>({ kind: 'idle' });
  const [attempt, setAttempt] = useState(0);
  // One stream-URL regeneration per file: a 401/403 from an expired signed URL triggers a
  // single list refresh (which mints fresh URLs); a second error is a real failure.
  const regenerated = useRef<Set<number>>(new Set());

  // The full transcript feed (CR-35): loaded lazily on the tab's first open, cached while
  // the recording stays open (the parent keys the watch window per recording).
  const full = useFullTranscript({
    recording,
    token: props.token,
    fetchImpl: props.fetchImpl,
    enabled: current.kind === 'full',
  });

  // The overview artifact: the signed STREAM URL goes straight into the player - native
  // buffering, seeking, no RAM blob (CR-29). Without a streamUrl (unsigned deployment) the
  // bearer blob path stays. Aborted switches cancel the blob transfer.
  useEffect(() => {
    if (activeFile === null || activePurpose === null || activeFile.status !== 'ready') {
      setMediaLoad({ kind: 'idle' });
      return undefined;
    }
    if (typeof activeFile.streamUrl === 'string' && activeFile.streamUrl.length > 0) {
      setMediaLoad({ kind: 'ready', url: activeFile.streamUrl, streamed: true });
      return undefined;
    }
    const controller = new AbortController();
    let revoked: string | null = null;
    setMediaLoad({ kind: 'loading', loaded: 0, total: null });
    fetchRecordingFileProgress(
      recording.roomSlug,
      recording.id,
      activeFile.id,
      props.token,
      (progress) => {
        if (!controller.signal.aborted) {
          setMediaLoad({ kind: 'loading', loaded: progress.loaded, total: progress.total });
        }
      },
      props.fetchImpl,
      controller.signal,
    )
      .then((blob) => {
        if (controller.signal.aborted) {
          return;
        }
        const url = URL.createObjectURL(blob);
        revoked = url;
        setMediaLoad({ kind: 'ready', url, streamed: false });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) {
          return;
        }
        setMediaLoad({ kind: 'error' });
      });
    return () => {
      controller.abort();
      if (revoked !== null) {
        URL.revokeObjectURL(revoked);
      }
    };
  }, [
    activeFile,
    activePurpose,
    recording.roomSlug,
    recording.id,
    props.token,
    props.fetchImpl,
    attempt,
  ]);

  // The Диалоги tab loads and renders the canonical .transcript.json artifact once ready.
  useEffect(() => {
    if (current.kind !== 'transcript') {
      return;
    }
    if (!transcriptReady || transcriptFile === null) {
      setTranscript({ kind: 'idle' });
      return;
    }
    const controller = new AbortController();
    let alive = true;
    setTranscript({ kind: 'loading' });
    fetchRecordingFile(
      recording.roomSlug,
      recording.id,
      transcriptFile.id,
      props.token,
      props.fetchImpl,
    )
      .then(async (blob) => {
        const parsed: unknown = JSON.parse(await blob.text()) as unknown;
        const doc = parseTranscript(parsed);
        if (alive && !controller.signal.aborted) {
          setTranscript(doc === null ? { kind: 'error' } : { kind: 'ready', doc });
        }
      })
      .catch(() => {
        if (alive && !controller.signal.aborted) {
          setTranscript({ kind: 'error' });
        }
      });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [current.kind, transcriptReady, transcriptFile, recording.roomSlug, recording.id, props.token, props.fetchImpl]);

  // The Саммари tab loads the summary text once its row is ready.
  useEffect(() => {
    if (current.kind !== 'summary') {
      return;
    }
    if (summaryFile === null || summaryFile.status !== 'ready') {
      setSummary({ kind: 'idle' });
      return;
    }
    const controller = new AbortController();
    let alive = true;
    setSummary({ kind: 'loading' });
    fetchRecordingFile(
      recording.roomSlug,
      recording.id,
      summaryFile.id,
      props.token,
      props.fetchImpl,
    )
      .then(async (blob) => {
        const text = await blob.text();
        if (alive && !controller.signal.aborted) {
          setSummary(text.trim().length === 0 ? { kind: 'error' } : { kind: 'ready', text });
        }
      })
      .catch(() => {
        if (alive && !controller.signal.aborted) {
          setSummary({ kind: 'error' });
        }
      });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [current.kind, summaryFile, recording.roomSlug, recording.id, props.token, props.fetchImpl]);

  /** One regeneration of an expired signed stream URL: refresh the list once per file. */
  const handleStreamError = useCallback((): void => {
    if (activeFile === null || regenerated.current.has(activeFile.id)) {
      setMediaLoad({ kind: 'error' });
      return;
    }
    regenerated.current.add(activeFile.id);
    props.onRefresh();
  }, [activeFile, props]);

  const activeReady = activeFile !== null && activeFile.status === 'ready';

  return (
    <div className="mx-auto flex max-w-[1000px] flex-col gap-4 px-4 py-5 sm:px-6">
      <div className="flex flex-wrap items-center gap-2">
        <h2
          data-testid="recordings-watch-title"
          className="min-w-0 font-mono text-[14px] font-semibold text-foreground"
        >
          /r/{recording.roomSlug} · #{String(recording.id)}
        </h2>
        <span
          className={cn(
            'rounded border px-1.5 py-0.5 text-[10px] font-medium',
            STATUS_TONE[recording.status],
          )}
        >
          {t(`recording.state_${recording.status}`)}
        </span>
        <span className="text-[11.5px] tabular-nums text-muted-foreground">
          {new Intl.DateTimeFormat(props.locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
            new Date(recording.startedAt),
          )}
        </span>
        {durationMs !== null ? (
          <span className="text-[11.5px] tabular-nums text-muted-foreground">
            {formatMeetingTimer(Math.round(durationMs / 1000))}
          </span>
        ) : null}
        <button
          type="button"
          data-testid="recordings-watch-close"
          aria-label={t('recordings.player_close_aria')}
          title={t('recordings.player_close_aria')}
          onClick={props.onClose}
          className="ml-auto grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X aria-hidden className="size-4" />
        </button>
      </div>
      {recording.error !== null ? (
        <p role="alert" className="text-[12px] text-red-300">
          {recording.error}
        </p>
      ) : null}

      <div
        className="flex flex-wrap gap-1.5"
        role="tablist"
        aria-label={t('recordings.tab_aria')}
      >
        {tabs.map((tab) => {
          const active = sameTab(tab, current);
          if (tab.kind === 'file') {
            const ready = tab.file.status === 'ready';
            return (
              <button
                key={tab.file.id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`rec-tab-${tab.purpose}-${String(tab.file.id)}`}
                onClick={() => {
                  setActiveTab(tab);
                  setAttempt((value) => value + 1);
                }}
                className={cn(
                  'flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[12px] font-medium transition-colors',
                  active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border text-foreground hover:bg-accent',
                )}
              >
                {t(`recording.purpose_${tab.purpose}`)}
                {ready ? (
                  tab.file.sizeBytes !== null ? (
                    <span
                      className={cn(
                        'tabular-nums',
                        active ? 'text-primary-foreground/70' : 'text-muted-foreground',
                      )}
                    >
                      {formatFileSize(tab.file.sizeBytes)}
                    </span>
                  ) : null
                ) : tab.file.status === 'missing' ? (
                  <span className={active ? 'text-primary-foreground/70' : 'text-red-300'}>
                    · {t('recordings.file_missing')}
                  </span>
                ) : (
                  <LoaderCircle
                    aria-hidden
                    className={cn('size-3 animate-spin', active ? '' : 'text-muted-foreground')}
                  />
                )}
              </button>
            );
          }
          return (
            <button
              key={tab.kind}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`rec-tab-${tab.kind}`}
              onClick={() => {
                setActiveTab(tab);
                setAttempt((value) => value + 1);
              }}
              className={cn(
                'flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[12px] font-medium transition-colors',
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-foreground hover:bg-accent',
              )}
            >
              {tab.kind === 'full'
                ? t('recordings.tab_full')
                : t(`recording.purpose_${tab.kind}`)}
              {tab.kind === 'transcript' && !transcriptReady ? (
                <LoaderCircle
                  aria-hidden
                  className={cn('size-3 animate-spin', active ? '' : 'text-muted-foreground')}
                />
              ) : null}
              {tab.kind === 'summary' && !(summaryFile !== null && summaryFile.status === 'ready') ? (
                <LoaderCircle
                  aria-hidden
                  className={cn('size-3 animate-spin', active ? '' : 'text-muted-foreground')}
                />
              ) : null}
            </button>
          );
        })}
      </div>

      <div
        data-testid="recording-player"
        className="overflow-hidden rounded-xl border border-border bg-background"
      >
        {current.kind === 'empty' ? (
          <div className="flex min-h-[280px] items-center justify-center p-6 text-center text-[13px] text-muted-foreground">
            {t('recordings.watch_no_media')}
          </div>
        ) : current.kind === 'transcript' ? (
          <TranscriptView
            state={transcript}
            transcriptReady={transcriptReady}
            onRetry={() => setAttempt((value) => value + 1)}
          />
        ) : current.kind === 'full' ? (
          <FullTranscriptView state={full} />
        ) : current.kind === 'summary' ? (
          <SummaryView
            state={summary}
            transcriptReady={transcriptReady}
            summaryStatus={summaryFile === null ? null : summaryFile.status}
            retryInFlight={props.summaryRetryInFlight}
            onRetry={() => setAttempt((value) => value + 1)}
            onRetrySummary={props.onRetrySummary}
          />
        ) : !activeReady ? (
          <div
            data-testid="recordings-player-pending"
            className="flex min-h-[280px] flex-col items-center justify-center gap-2 p-6 text-center"
          >
            <LoaderCircle aria-hidden className="size-5 animate-spin text-muted-foreground" />
            <p className="text-[13px] text-muted-foreground">
              {activeFile?.status === 'missing'
                ? t('recordings.file_missing')
                : activePurpose === 'overview'
                  ? t('recordings.overview_preparing')
                  : t('recordings.artifact_preparing')}
            </p>
          </div>
        ) : mediaLoad.kind === 'error' ? (
          <div
            data-testid="recordings-player-error"
            className="flex min-h-[280px] flex-col items-center justify-center gap-3 p-6 text-center"
          >
            <AlertCircle aria-hidden className="size-5 text-red-300" />
            <p className="text-[13px] text-muted-foreground">{t('recordings.player_failed')}</p>
            <button
              type="button"
              data-testid="recordings-player-retry"
              onClick={() => {
                setMediaLoad({ kind: 'idle' });
                setAttempt((value) => value + 1);
              }}
              className="rounded-lg border border-border px-3 py-1.5 text-[12.5px] font-medium text-foreground transition-colors hover:bg-accent"
            >
              {t('recordings.retry')}
            </button>
          </div>
        ) : mediaLoad.kind === 'loading' ? (
          <div
            data-testid="recordings-player-progress"
            role="progressbar"
            aria-label={t('recordings.progress_aria')}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={
              mediaLoad.total !== null && mediaLoad.total > 0
                ? Math.min(100, Math.round((mediaLoad.loaded / mediaLoad.total) * 100))
                : undefined
            }
            className="flex min-h-[280px] flex-col items-center justify-center gap-3 p-6"
          >
            <LoaderCircle aria-hidden className="size-5 animate-spin text-muted-foreground" />
            <div className="h-1.5 w-full max-w-[360px] overflow-hidden rounded-full bg-muted">
              <div
                data-testid="recordings-player-progress-bar"
                className="h-full rounded-full bg-primary transition-[width] duration-300"
                style={{
                  width:
                    mediaLoad.total !== null && mediaLoad.total > 0
                      ? `${String(Math.min(100, (mediaLoad.loaded / mediaLoad.total) * 100))}%`
                      : '25%',
                }}
              />
            </div>
            <p className="text-[11.5px] tabular-nums text-muted-foreground">
              {formatFileSize(mediaLoad.loaded)}
              {mediaLoad.total !== null ? ` / ${formatFileSize(mediaLoad.total)}` : ''}
            </p>
          </div>
        ) : mediaLoad.kind === 'ready' ? (
          /* The signed stream URL (or the blob fallback) rides the video.js source; the player
           * chunk is lazy, so the first paint of a ready artifact is a spinner frame. */
          <Suspense
            fallback={
              <div className="flex min-h-[280px] items-center justify-center p-6">
                <LoaderCircle aria-hidden className="size-5 animate-spin text-muted-foreground" />
              </div>
            }
          >
            <RecordingPlayer
              title={`/r/${recording.roomSlug} · #${String(recording.id)}`}
              src={mediaLoad.url}
              onMediaError={
                mediaLoad.streamed
                  ? handleStreamError
                  : (): void => setMediaLoad({ kind: 'error' })
              }
            />
          </Suspense>
        ) : null}
      </div>

      {activeFile !== null && activeReady ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid={`rec-download-${activePurpose}-${String(activeFile.id)}`}
            aria-label={t('recording.download_aria', {
              purpose: t(`recording.purpose_${activePurpose}`),
            })}
            disabled={props.downloadingFileId !== null}
            onClick={() => {
              props.onDownload(activeFile);
            }}
            className="flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-[12.5px] font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Download aria-hidden className="size-3.5" />
            {props.downloadingFileId === activeFile.id
              ? t('recording.downloading')
              : t('recording.download')}
            {activeFile.sizeBytes !== null ? (
              <span className="tabular-nums text-muted-foreground">
                {formatFileSize(activeFile.sizeBytes)}
              </span>
            ) : null}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Load state of the watch window's active artifact. */
type MediaLoad =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading'; readonly loaded: number; readonly total: number | null }
  | { readonly kind: 'ready'; readonly url: string; readonly streamed: boolean }
  | { readonly kind: 'error' };

/** Transcript (Диалоги) load state: idle = the transcription has not finished yet. */
type TranscriptLoad =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly doc: TranscriptDoc }
  | { readonly kind: 'error' };

/** Summary (Саммари) load state: idle = the artifact is not ready (the row state drives the notice). */
type SummaryLoad =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly text: string }
  | { readonly kind: 'error' };

/**
 * The Диалоги view: the transcript rendered as a timed dialogue - one scrollable row per
 * segment (timestamp + text). Until the transcription finishes, the transcribing state shows.
 */
function TranscriptView(props: {
  readonly state: TranscriptLoad;
  readonly transcriptReady: boolean;
  readonly onRetry: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  if (!props.transcriptReady || props.state.kind === 'idle') {
    return (
      <div
        data-testid="recordings-transcribing"
        className="flex min-h-[280px] flex-col items-center justify-center gap-2 p-6 text-center"
      >
        <LoaderCircle aria-hidden className="size-5 animate-spin text-muted-foreground" />
        <p className="text-[13px] text-muted-foreground">{t('recordings.transcribing')}</p>
      </div>
    );
  }
  if (props.state.kind === 'loading') {
    return (
      <div className="flex min-h-[280px] items-center justify-center p-6">
        <LoaderCircle aria-hidden className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (props.state.kind === 'error') {
    return (
      <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-[13px] text-muted-foreground">{t('recordings.transcript_failed')}</p>
        <button
          type="button"
          onClick={props.onRetry}
          className="rounded-lg border border-border px-3 py-1.5 text-[12.5px] font-medium text-foreground transition-colors hover:bg-accent"
        >
          {t('recordings.retry')}
        </button>
      </div>
    );
  }
  if (props.state.doc.segments.length === 0) {
    return (
      <div className="flex min-h-[280px] items-center justify-center p-6 text-center text-[13px] text-muted-foreground">
        {t('recordings.transcript_empty')}
      </div>
    );
  }
  return (
    <div data-testid="recordings-dialogues" className="scroll-thin max-h-[62vh] overflow-y-auto">
      <ul className="divide-y divide-border">
        {props.state.doc.segments.map((segment, index) => (
          <li key={String(index)} className="flex gap-3 px-4 py-2.5">
            <span className="shrink-0 pt-0.5 font-mono text-[11.5px] tabular-nums text-muted-foreground">
              {transcriptTime(segment.start)}
            </span>
            <span className="text-[13px] leading-relaxed text-foreground">{segment.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The Саммари view (CR-30): the DeepSeek summary text once ready, otherwise the honest
 * pipeline states - transcribing, preparing (with a retry for a dead job), the no-key hint,
 * or the error + «Повторить».
 */
function SummaryView(props: {
  readonly state: SummaryLoad;
  readonly transcriptReady: boolean;
  readonly summaryStatus: RecordingFile['status'] | null;
  readonly retryInFlight: boolean;
  readonly onRetry: () => void;
  readonly onRetrySummary: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  if (!props.transcriptReady) {
    return (
      <div
        data-testid="recordings-summary-transcribing"
        className="flex min-h-[280px] flex-col items-center justify-center gap-2 p-6 text-center"
      >
        <LoaderCircle aria-hidden className="size-5 animate-spin text-muted-foreground" />
        <p className="text-[13px] text-muted-foreground">{t('recordings.transcribing')}</p>
      </div>
    );
  }
  if (props.state.kind === 'loading') {
    return (
      <div className="flex min-h-[280px] items-center justify-center p-6">
        <LoaderCircle aria-hidden className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (props.state.kind === 'error') {
    return (
      <div
        data-testid="recordings-summary-error"
        className="flex min-h-[280px] flex-col items-center justify-center gap-3 p-6 text-center"
      >
        <AlertCircle aria-hidden className="size-5 text-red-300" />
        <p className="text-[13px] text-muted-foreground">{t('recordings.summary_failed')}</p>
        <button
          type="button"
          data-testid="recordings-summary-retry"
          onClick={props.onRetry}
          className="rounded-lg border border-border px-3 py-1.5 text-[12.5px] font-medium text-foreground transition-colors hover:bg-accent"
        >
          {t('recordings.retry')}
        </button>
      </div>
    );
  }
  if (props.state.kind === 'ready') {
    return (
      <div data-testid="recordings-summary" className="scroll-thin max-h-[62vh] overflow-y-auto p-4">
        <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground">
          {props.state.text}
        </p>
      </div>
    );
  }
  if (props.summaryStatus === 'no_key') {
    return (
      <div
        data-testid="recordings-summary-no-key"
        className="flex min-h-[280px] flex-col items-center justify-center gap-2 p-6 text-center"
      >
        <p className="text-[13px] text-muted-foreground">{t('recordings.summary_no_key')}</p>
      </div>
    );
  }
  // No ready row yet: the summary is being prepared - with a retry for a job that died
  // terminally (the re-enqueue endpoint answers idempotently while one is in flight).
  return (
    <div
      data-testid="recordings-summary-preparing"
      className="flex min-h-[280px] flex-col items-center justify-center gap-3 p-6 text-center"
    >
      <LoaderCircle aria-hidden className="size-5 animate-spin text-muted-foreground" />
      <p className="text-[13px] text-muted-foreground">{t('recordings.summary_preparing')}</p>
      <button
        type="button"
        data-testid="recordings-summary-retry"
        disabled={props.retryInFlight}
        onClick={props.onRetrySummary}
        className="rounded-lg border border-border px-3 py-1.5 text-[12.5px] font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
      >
        {t('recordings.retry')}
      </button>
    </div>
  );
}
